import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useMemo,
} from 'react';

import { scrollToEndOfElement } from './product-configuration.utils';

interface ScrollContextProps {
  hasRequiredOptionScrolled: boolean;
  hasNonRequiredOptionScrolled: boolean;
  setRequiredOptionScrolled: (value: boolean) => void;
  setNonRequiredOptionScrolled: (value: boolean) => void;
  scrollRequiredOption: (parentId: string) => void;
  scrollNonRequiredOption: (parentId: string) => void;
}

const ScrollContext = createContext<ScrollContextProps | undefined>(undefined);

export const ScrollProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [hasRequiredOptionScrolled, setRequiredOptionScrolled] =
    useState<boolean>(false);
  const [hasNonRequiredOptionScrolled, setNonRequiredOptionScrolled] =
    useState<boolean>(false);

  const scrollRequiredOption = (parentId: string) => {
    if (!hasRequiredOptionScrolled) {
      scrollToEndOfElement({ elementId: parentId });
      setRequiredOptionScrolled(true);
    }
  };

  const scrollNonRequiredOption = (parentId: string) => {
    if (!hasNonRequiredOptionScrolled) {
      scrollToEndOfElement({ elementId: parentId, offsetHeader: true });
      setNonRequiredOptionScrolled(true);
    }
  };

  const value = useMemo(
    () => ({
      hasRequiredOptionScrolled,
      hasNonRequiredOptionScrolled,
      setRequiredOptionScrolled,
      setNonRequiredOptionScrolled,
      scrollRequiredOption,
      scrollNonRequiredOption,
    }),
    [
      hasRequiredOptionScrolled,
      hasNonRequiredOptionScrolled,
      setRequiredOptionScrolled,
      setNonRequiredOptionScrolled,
      scrollRequiredOption,
      scrollNonRequiredOption,
    ]
  );

  return (
    <ScrollContext.Provider value={value}>{children}</ScrollContext.Provider>
  );
};

export const useScrollContext = (): ScrollContextProps => {
  const context = useContext(ScrollContext);
  if (!context) {
    throw new Error('useScrollContext must be used within a ScrollProvider');
  }
  return context;
};
